import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { PersonAvatar, cx } from '../../components/ui';
import type { Person } from '../../types';

const generationNames = new Map([
  [15, '十五世'],
  [16, '十六世'],
  [17, '十七世'],
  [18, '十八世'],
  [19, '十九世']
]);

type FamilyNode = Node<{ person: Person; active: boolean; onSelect: (person: Person) => void }, 'familyPerson'>;

function FamilyNodeCard({ data }: NodeProps<FamilyNode>) {
  return (
    <button type="button" className={cx('family-node-card', data.active && 'active')} onClick={() => data.onSelect(data.person)}>
      <Handle id="parent-target" type="target" position={Position.Top} className="family-handle" />
      <Handle id="child-source" type="source" position={Position.Bottom} className="family-handle" />
      <Handle id="spouse-left" type="target" position={Position.Left} className="family-handle spouse" />
      <Handle id="spouse-right" type="source" position={Position.Right} className="family-handle spouse" />
      <PersonAvatar person={data.person} />
      <span className="family-node-copy">
        <strong>{data.person.name}</strong>
        <small>{data.person.birth}{data.person.death ? `-${data.person.death}` : '-'}</small>
      </span>
    </button>
  );
}

const nodeTypes = { familyPerson: FamilyNodeCard };

function buildNodes(people: Person[], selectedId: string, onSelect: (person: Person) => void): FamilyNode[] {
  const rows = new Map<number, Person[]>();
  people.forEach((person) => rows.set(person.generation, [...(rows.get(person.generation) ?? []), person]));
  const generations = [...rows.keys()].sort((left, right) => left - right);

  return generations.flatMap((generation, rowIndex) => {
    const row = rows.get(generation)!.sort((left, right) => left.x - right.x || left.name.localeCompare(right.name));
    return row.map((person, columnIndex) => ({
      id: person.id,
      type: 'familyPerson',
      position: { x: 260 + columnIndex * 248, y: 70 + rowIndex * 184 },
      data: { person, active: person.id === selectedId, onSelect },
      draggable: false
    }));
  });
}

function buildEdges(people: Person[], nodes: FamilyNode[]): Edge[] {
  const visibleIds = new Set(people.map((person) => person.id));
  const positions = new Map(nodes.map((node) => [node.id, node.position.x]));
  const spouseEdges = new Set<string>();
  const edges: Edge[] = [];

  people.forEach((person) => {
    person.childIds.forEach((childId) => {
      if (!visibleIds.has(childId)) return;
      edges.push({
        id: `child-${person.id}-${childId}`,
        source: person.id,
        target: childId,
        sourceHandle: 'child-source',
        targetHandle: 'parent-target',
        type: 'smoothstep',
        style: { stroke: 'rgba(208,231,224,.54)', strokeWidth: 1.5 }
      });
    });

    person.spouseIds.forEach((spouseId) => {
      if (!visibleIds.has(spouseId)) return;
      const key = [person.id, spouseId].sort().join('-');
      if (spouseEdges.has(key)) return;
      spouseEdges.add(key);
      const source = (positions.get(person.id) ?? 0) <= (positions.get(spouseId) ?? 0) ? person.id : spouseId;
      const target = source === person.id ? spouseId : person.id;
      edges.push({
        id: `spouse-${key}`,
        source,
        target,
        sourceHandle: 'spouse-right',
        targetHandle: 'spouse-left',
        type: 'straight',
        style: { stroke: '#d8ad63', strokeWidth: 2 }
      });
    });
  });

  return edges;
}

export function FamilyTreeCanvas({ people, selected, onSelect }: { people: Person[]; selected: Person; onSelect: (person: Person) => void }) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const visiblePeople = useMemo(() => people.filter((person) => !collapsed.has(person.generation)), [people, collapsed]);
  const nodes = useMemo(() => buildNodes(visiblePeople, selected.id, onSelect), [visiblePeople, selected.id, onSelect]);
  const edges = useMemo(() => buildEdges(visiblePeople, nodes), [visiblePeople, nodes]);

  const toggleGeneration = (generation: number) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(generation)) next.delete(generation);
      else next.add(generation);
      return next;
    });
  };

  return (
    <section className="tree-surface">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.14, minZoom: 0.42, maxZoom: 0.92 }}
        minZoom={0.28}
        maxZoom={1.6}
        nodesDraggable={false}
        nodesConnectable={false}
        panOnDrag
        panOnScroll={false}
        zoomOnScroll
        zoomOnPinch
        preventScrolling
        selectionOnDrag={false}
        selectNodesOnDrag={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="rgba(213,234,226,.07)" gap={40} size={1} />
        <Controls showInteractive={false} position="bottom-center" />
        <MiniMap
          position="bottom-left"
          pannable
          zoomable
          nodeColor={(node) => node.id === selected.id ? '#64ddbd' : 'rgba(211,232,224,.5)'}
          maskColor="rgba(5,39,47,.62)"
        />
      </ReactFlow>

      <div className="generation-rail">
        {[15, 16, 17, 18, 19].map((generation) => (
          <button key={generation} type="button" className={cx(generation === selected.generation && 'active')} onClick={() => toggleGeneration(generation)}>
            <span>{collapsed.has(generation) ? <ChevronRight /> : <ChevronDown />}</span>
            {generationNames.get(generation) ?? `${generation}世`}
          </button>
        ))}
      </div>
    </section>
  );
}
